import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useLocation, useParams } from "react-router-dom";
import api from "../../../../services/api";
import qs from "qs";
import {
    Container, Row, Col, Card, CardBody, CardTitle
} from "reactstrap";
import moment from "moment";
import Loader from "../../../loaders";
import { formatDateTime } from '../../../../utils';
import { ParagraphNotification } from '../../../alerts';
import { forEach } from 'lodash';
import { StripName } from '../../../../helpers/StringHelper';

const timeoutLength = 1500;
const apiurl = "rostercandidate/tnadiscrepancyreport";
const pivotGroupHeaderHeight = 50;
const pivotHeaderHeight = 100;

function getTextWidth(text, font, size) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    context.font = font || getComputedStyle(document.body).font;

    return context.measureText(text).width;
}

export default function TnaDRGrid(props) {
    const params = useParams();
    const location = useLocation();
    const [rawData, setRawData] = useState(null);
    const [data, setData] = useState([]);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [loading, setLoading] = useState(false);
    const [itemInEdit, setItemInEdit] = useState(null);
    const [createNew, setCreateNew] = useState(false);
    const [canAutoOpen, setCanAutoOpen] = useState(true);
    const [downloadAlert, setDownloadAlert] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalRecords, setTotalRecords] = useState(0);
    const [deleteAlert, setDeleteAlert] = useState([]);
    const [lookups, setLookups] = useState({});
    const [filters, setFilters] = useState([]);

    const defaultSort = useMemo(() => [{ id: "createdDate", desc: true }],
        []
    );

    const [sortBy, setSortBy] = useState(defaultSort);
    const [pnMessage, setPnMessage] = useState(null);
    const [pnTitle, setPnTitle] = useState(null);
    const [paragraphNotification, setParagraphNotification] = useState([]);
    const [pnTitleOnGenerate, setPnTitleOnGenerate] = useState(``);
    const [gridOptions, setGridOptions] = useState(null);
    const [headerHeight, setHeaderHeight] = useState(30);

    const skipPageResetRef = useRef();
    const gridRef = useRef();

    useEffect(() => {
        if (copiedIndex !== null) {
            const timeoutId = setTimeout(() => {
                setCopiedIndex(null);
            }, timeoutLength);

            return () => clearTimeout(timeoutId);
        }
    }, [copiedIndex]);

    const resizeColumns = useCallback(() => {
        if (!gridRef?.current?.columnApi || data?.length === 0)
            return;

        const allColumnIds = [];
        gridRef.current.columnApi.getColumns().forEach((column) => {
            // console.log(column, column.getId());
            allColumnIds.push(column.getId());
        });
        gridRef.current.columnApi.autoSizeColumns(allColumnIds, false);
    }, [data, gridRef]);

    const defaultColumns = [
        {
            name: "candidateName",
            title: "Candidate Name"
        },
        {
            name: "positionName",
            title: "Position Name"
        },
    ];

    const columnDefs = useMemo(() => {
        let newMap = defaultColumns.map((c) => {
            const obj = {
                ...c,
                field: c.name,
                headerName: c.title,
            };

            if ('name' in obj) delete obj.name;
            if ('title' in obj) delete obj.title;

            return obj;
        });

        if (rawData) {
            const excludeCol = ['candidate name', 'position'];
            const { trainingTitleColumns } = rawData;
            const colToAdd = trainingTitleColumns.filter(w => w && !excludeCol.some(e => e == w.toLowerCase())).map(v => ({
                field: StripName(v),
                headerName: v,
                headerClass: 'ag-vheader',
                width: '50px',
                valueFormatter: (params) => {
                    var { value } = params;
                    return value == "0" || value == "1" ? "-" : !value ? "" : moment(formatDateTime(value)).format("DD-MM-YYYY");
                },
                cellClass: (params) => {
                    const addonClass = 'text-center';
                    const { value } = params;

                    if (value == "0") {
                        return `pink ${addonClass}`;
                    } else if (value) {
                        return `cyan ${addonClass}`;
                    } else {
                        return ` ${addonClass}`;
                    }
                },
            }));

            newMap = newMap.concat(colToAdd);
            const longestText = newMap.map(v => v.headerName).reduce(
                function (a, b) {
                    return a.length > b.length ? a : b;
                }
            );

            const font = '10px Roboto'
            const height = getTextWidth(longestText, font);
            setHeaderHeight(height);
        }

        return newMap;
    }, [rawData]);

    const defaultColDef = useMemo(() => ({
        resizable: true,
        editable: false,
        filter: '',
        sortable: false,
        floatingFilter: false,
        suppressMenu: true,
    }), []);

    const loadingOverlayComponent = useMemo(() => Loader, []);

    useEffect(() => {
        if (params.action === "new")
            setCreateNew(true);
    }, [params.action]);

    const loadData = useCallback((params2 = null) => {
        if (params2 && 'api' in params2) {
            const { api: api2 } = params2;
            api2.hideOverlay();
        }
        
        skipPageResetRef.current = true;
        setItemInEdit(null);
        setCreateNew(false);
        setData([]);

        const queryObj = qs.parse(location.search, { ignoreQueryPrefix: true });

        if (!queryObj || !queryObj.projectId)
            return;

        const candidateIds = queryObj?.candidateIds ? decodeURI(queryObj.candidateIds).split("-") : [];
        const url = `/${apiurl}?projectId=${queryObj.projectId}&dispatchUnitId=${queryObj?.dispatchUnitId ?? 0}${queryObj.rosterId ? `&rosterId=${queryObj.rosterId}` : ""}`;
        setLoading(true);

        api.post(url, JSON.stringify(candidateIds))
            .then(({ data }) => {
                setRawData(data);
            })
            .catch((error) => {
                setItemInEdit(null);
                setRawData([]);
                console.error("error: ", { error });
            })
            .finally(() => setLoading(false));
    }, [filters, sortBy, pageNumber, pageSize, params]);

    useEffect(() => {
        if (canAutoOpen && data && data.length === 1 && params.requestId && params.action && params.action === "joborder") {
            setItemInEdit(data[0]);
        }
    }, [canAutoOpen, data, params]);

    useEffect(() => {
        if (!rawData || !rawData.rows || !rawData.rows.length) {
            setData([]);
            setTotalRecords(0);
            return;
        }
        
        const {
            rows
        } = rawData;

        if (!rows?.length) {
            setData([]);
            setTotalRecords(0);
            return;
        }

        const newData = rows.map((v, i, a) => {
            const oneRow = {
                candidateName: v.candidateName,
                positionName: v.positionName,
                candidateId: v.columns && v.columns.length > 0 ? v.columns[0].candidateId : null
            }

            if (v.columns && v.columns.length) {
                const drawableColumns = v.columns.filter(w => w && w.skillTitle);

                forEach(drawableColumns, (oneCol) => {
                    var colKey = StripName(oneCol.skillTitle);
                    oneRow[colKey] = oneCol.expiredDate ? oneCol.expiredDate : oneCol.result ? "1" : "0";
                });
            }

            return oneRow;
        });

        setTotalRecords(newData?.length ?? 0);
        setData(newData);
    }, [rawData]);
    
    useEffect(() => {
        if (!pnMessage) {
            setParagraphNotification([]);
            return;
        }

        const events = {
            onConfirm: () => {
                setPnMessage(null);
                setPnTitle(null);
            },
            message: pnMessage,
            title: pnTitle ?? pnTitleOnGenerate
        }

        setParagraphNotification([<ParagraphNotification {...events} />]);
    }, [pnMessage, pnTitle]);
    
    useEffect(() => {
        const apiCalls = [
            api.get(`/lookup/messagesmasters`),
        ];

        Promise.all(apiCalls)
            .then((responses) => {
                setLookups({
                    messagesMaster: responses[0].data
                });
                loadData();
            })
            .catch((error) => {
                console.error("error: ", { error });
            });
    }, []);

    return (
        <section className="main">
            {loading && <Loader />}
            {deleteAlert.length > 0 && deleteAlert}
            {downloadAlert.length > 0 && downloadAlert}
            {paragraphNotification.length > 0 && paragraphNotification}
            <Container fluid>
                <Row>
                    <Col xs={12}>
                        <Card className="no-transition">
                            <CardBody>
                                <CardTitle>
                                    {/*<h5 className="mb-0 text-center">
                                        TNA Discrepancy Report
                                    </h5>*/}
                                    <h6 className="mb-0">
                                        Project: {rawData?.projectName}
                                    </h6>
                                </CardTitle>

                                <Row>
                                    <Col sm={12}>
                                        <div className="ag-theme-alpine ag-grid ag-ignore-disabled-input ag-vertical-columns" style={{ width: '100%', height: 'calc(100vh - 200px)' }}>
                                            <AgGridReact
                                                ref={gridRef}
                                                gridOptions={gridOptions}
                                                //domLayout={'autoHeight'}
                                                rowData={data}
                                                columnDefs={columnDefs}
                                                defaultColDef={defaultColDef}
                                                loadingOverlayComponent={loadingOverlayComponent}
                                                //sideBar={sideBar}
                                                suppressColumnVirtualisation={false}
                                                suppressRowVirtualisation={false}
                                                debounceVerticalScrollbar={false}
                                                rowBuffer={20}
                                                headerHeight={headerHeight}
                                                groupHeaderHeight={30}
                                                rowHeight={30}
                                                floatingFiltersHeight={30}
                                                // readOnlyEdit={true}
                                                onRowDataUpdated={() => resizeColumns()}
                                                suppressDragLeaveHidesColumns={true}
                                                suppressColumnMoveAnimation={true}
                                                animateRows={false}
                                                //onCellEditRequest={onCellEditRequest}
                                                excelStyles={excelStyles()}
                                                //pagination={true}
                                                //paginationPageSize={pageSize}
                                                //cacheBlockSize={pageSize}
                                                //rowModelType={'serverSide'}
                                                //serverSideInfiniteScroll={true}
                                                //onGridReady={onGridReady}
                                                //sortingOrder={agGridSort}
                                                pivotGroupHeaderHeight={pivotGroupHeaderHeight}
                                                pivotHeaderHeight={pivotHeaderHeight}
                                            />
                                        </div>
                                    </Col>
                                </Row>

                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

        </section>
    )

}


export const excelStyles = () => {
    const headerDefaultStyle = {
        alignment: {
            vertical: 'Center',
            horizontal: "Center",
        },
        interior: {
            color: '#f8f8f8',
            pattern: 'Solid',
            patternColor: undefined,
        },
        borders: {
            borderTop: {
                color: '#babfc7',
                lineStyle: 'Continuous',
                weight: 1,
            },
            borderRight: {
                color: '#babfc7',
                lineStyle: 'Continuous',
                weight: 1,
            },
            borderBottom: {
                color: '#babfc7',
                lineStyle: 'Continuous',
                weight: 1,
            },
            borderLeft: {
                color: '#babfc7',
                lineStyle: 'Continuous',
                weight: 1,
            },
        },
        font: {
            fontName: 'Roboto',
            color: '#181d1f',
            bold: true,
            size: 12,
        },
    }

    return [
        {
            id: 'header',
            ...headerDefaultStyle
        },
        {
            id: 'date-header',
            ...headerDefaultStyle,
            alignment: {
                vertical: 'Center',
                horizontal: "Center",
                rotate: 90,
            },
        },
        {
            id: 'cell',
            alignment: {
                vertical: 'Top',
                wrapText: true,
            },
            interior: {
                color: '#ffffff',
                pattern: 'Solid',
                patternColor: undefined,
            },
            borders: {
                borderTop: {
                    color: '#babfc7',
                    lineStyle: 'Continuous',
                    weight: 1,
                },
                borderRight: {
                    color: '#babfc7',
                    lineStyle: 'Continuous',
                    weight: 1,
                },
                borderBottom: {
                    color: '#babfc7',
                    lineStyle: 'Continuous',
                    weight: 1,
                },
                borderLeft: {
                    color: '#babfc7',
                    lineStyle: 'Continuous',
                    weight: 1,
                },
            },
            font: {
                fontName: 'Roboto',
                color: '#181d1f',
                weight: 700,
                size: 12,
            },
        },
    ];
};


